import React from "react";
import {
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../components/formsComponents";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import produce from "immer";
import { IBusinessPolicyChangeStepsProps } from "../PolicyChange.types";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { AddOutlined } from "@mui/icons-material";
import { IBusinessPolicyEquipment } from "../../../../../../../redux/businessPolicy/businessPolicyEquipment/businessPolicyEquipment.types";
import { EquipmentRightPanel } from "../../Equipment/EquipmentRightPanel";
import { useBusinessPolicyEquipment } from "../../hooks/useBusinessPolicyEquiment";

export const EquipmentChange: React.FC<IBusinessPolicyChangeStepsProps> = ({
  values,
  setValues,
  errors,
  handleChange,
  setFieldValue,
  customerId,
  customerPolicyId,
  isStatusCompleted,
  autoSaveData = () => {},
}) => {
  const [open, setOpen] = React.useState<{
    type: "create" | "edit" | "view";
    index?: number;
    data?: IBusinessPolicyEquipment;
  } | null>(null);
  const theme = useTheme();
  const { equipmentDropdown, refreshData } =
    useBusinessPolicyEquipment(customerPolicyId);

  const handleCheckBoxChange = (value: string) => {
    setFieldValue("equipment_change.type", value);
  };

  const handleSaveSuccess = (equipment: IBusinessPolicyEquipment) => {
    const newValues = produce(values, (draftValues) => {
      const updatedData = {
        ...equipment,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
      };
      if (open && open.type === "edit" && open.index !== undefined) {
        draftValues.equipment_change.records[open.index] = updatedData;
      } else {
        draftValues.equipment_change.records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    setOpen(null);
    refreshData();
  };

  const handleCreateEdit = (index?: number) => () => {
    if (isStatusCompleted) {
      return;
    }
    setOpen({
      type: index !== undefined ? "edit" : "create",
      index: index,
      data:
        index !== undefined
          ? values.equipment_change.records[index]
          : undefined,
    });
  };

  const handleView = (index: number) => () => {
    setOpen({
      type: "view",
      index: index,
      data: values.equipment_change.records[index],
    });
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
  const handleDelete = (index: number) => () => {
    setDeleteConfirm(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm === null) {
      return;
    }
    const newValues = produce(values, (draftValues) => {
      draftValues.equipment_change.records.splice(deleteConfirm, 1);
    });
    setValues(newValues);
    autoSaveData(newValues);
    setDeleteConfirm(null);
  };

  return (
    <>
      <LabelCard heading="Equipment Change" />
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.equipment_change.type === "Add"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Add" : "");
              }}
            />
            <Typography>Add</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.equipment_change.type === "Delete"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Delete" : "");
              }}
            />
            <Typography>Delete</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.equipment_change.type === "Change"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Change" : "");
              }}
            />
            <Typography>Substitution</Typography>
          </Stack>
        </Grid>
      </Grid>

      {values.equipment_change.type &&
        ["Add", "Change"].includes(values.equipment_change.type) && (
          <>
            <Grid container spacing={3}>
              {values.equipment_change.type === "Change" && (
                <Grid item xs={12} md={6}>
                  <CustomFormLabel>Delete Equipment</CustomFormLabel>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    name="equipment_change.delete_equipment"
                    fullWidth
                    displayEmpty
                    value={Number(values.equipment_change.delete_equipment)}
                    onChange={handleChange}
                    placeholder="Select One"
                    options={equipmentDropdown}
                  ></ControlledCustomSelect>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack direction={"row"} justifyContent={"end"}>
                  <Button
                    variant="contained"
                    disabled={isStatusCompleted}
                    onClick={handleCreateEdit()}
                    startIcon={<AddOutlined />}
                  >
                    New
                  </Button>
                </Stack>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>View</TableCell>
                        <TableCell>Location #</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Limit Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {values.equipment_change.records.map((item, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ display: "flex" }}>
                              <StandardTableActions
                                onViewClick={handleView(index)}
                                onEditClick={
                                  !isStatusCompleted
                                    ? handleCreateEdit(index)
                                    : undefined
                                }
                                // onDeleteClick={
                                //   !isStatusCompleted
                                //     ? handleDelete(index)
                                //     : undefined
                                // }
                              />
                            </TableCell>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{item.effective_date}</TableCell>
                            <TableCell>{item.serial_number}</TableCell>
                            <TableCell>{item.limit_amount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </>
        )}

      {values.equipment_change.type &&
        ["Delete"].includes(values.equipment_change.type) && (
          <Grid container>
            <Grid item xs={12} md={6}>
              <CustomFormLabel>Delete Equipment</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="equipment_change.delete_equipment"
                fullWidth
                displayEmpty
                value={Number(values.equipment_change.delete_equipment)}
                onChange={handleChange}
                placeholder="Select One"
                options={equipmentDropdown}
              ></ControlledCustomSelect>
            </Grid>
          </Grid>
        )}

      {open && (
        <EquipmentRightPanel
          open={true}
          readonly={open.type === "view"}
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          equipmentNumber={open.data ? open.data.incremental_number as any : undefined}
          onSaveSuccess={handleSaveSuccess}
          //staticStatusValue="INACTIVE"
          created_from="Policy_Change"
          onClose={() => setOpen(null)}
          //initialData={open.data}
          //disableApiCalls
        />
      )}
      {deleteConfirm !== null && (
        <ConfirmDialog
          open
          content="Are you sure you want to delete this record?"
          onClose={() => setDeleteConfirm(null)}
          onConfrim={handleDeleteConfirm}
        />
      )}
    </>
  );
};
